import type { DialogProps } from './Dialog.typed';
import Overlay from '../Overlay/Overlay';
import Icon from '../Icon/Icon';
import * as Styled from './Dialog.styled';
import { CSSTransition } from 'react-transition-group';
import { useIsomorphicLayoutEffect } from 'hooks';
import { useRef } from 'react';

const ANIMATION_TIME_IN_MS = 300;

const Dialog = (props: DialogProps) => {
  const nodeRef = useRef(null);
  const {
    isOpen,
    fullScreen = false,
    children,
    askToClose,
    footer,
    header,
    showClose = true,
    returnFocusRef,
    anchor = 'center',
    size = 's',
    ...rest
  } = props;

  const modifiedAnchor = typeof anchor === 'string' ? { xs: anchor } : anchor;

  useIsomorphicLayoutEffect(() => {
    if (isOpen) {
      window.document.body.classList.add('is-frozen');
    } else {
      window.document.body.classList.remove('is-frozen');
    }
    return () => {
      window.document.body.classList.remove('is-frozen');
    };
  }, [isOpen]);

  return (
    <CSSTransition
      ref={nodeRef}
      mountOnEnter={true}
      unmountOnExit={true}
      in={isOpen}
      timeout={ANIMATION_TIME_IN_MS}
    >
      {(state) => (
        <Overlay
          returnFocusRef={returnFocusRef}
          shouldTrapFocus={true}
          onClickOutside={askToClose}
          onEscape={askToClose}
        >
          <>
            {!fullScreen && (
              <Styled.Backdrop
                state={state}
                onClick={askToClose}
              ></Styled.Backdrop>
            )}
            <Styled.Dialog
              {...rest}
              hasChildren={!!children}
              anchor={modifiedAnchor}
              fullScreen={fullScreen}
              size={size}
              state={state}
              animationTimeInMS={ANIMATION_TIME_IN_MS}
              role="dialog"
            >
              {(!!header || !!showClose) && (
                <Styled.Header>
                  <Styled.HeaderContent>
                    {!!header && header}
                  </Styled.HeaderContent>
                  {showClose && (
                    <Styled.Close aria-label="close" onClick={askToClose}>
                      <Icon name="CloseOutline" size="s" color="#999" />
                    </Styled.Close>
                  )}
                </Styled.Header>
              )}
              {!!children && <Styled.Body>{children}</Styled.Body>}
              {!!footer && <Styled.Footer>{footer}</Styled.Footer>}
            </Styled.Dialog>
          </>
        </Overlay>
      )}
    </CSSTransition>
  );
};

Dialog.Actions = Styled.Actions;

export default Dialog;
